import {
  getSampleOrganization,
  type OrganizationMembership,
} from "@fleetminder/contracts";
import {
  Alert,
  AlertDescription,
  AlertTitle,
  Badge,
  Button,
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuLabel,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
  SidebarMenuButton,
  Spinner,
} from "@fleetminder/ui";

import { useQuery, useQueryClient } from "@tanstack/react-query";
import {
  BuildingIcon,
  CheckIcon,
  ChevronsUpDownIcon,
  CompassIcon,
  PlusIcon,
} from "lucide-react";
import { type PropsWithChildren, useCallback, useEffect } from "react";
import { Navigate, useNavigate, useParams } from "react-router-dom";

import { callApi, queryKeys, WebApiError } from "./api-client.js";
import { roleLabels } from "./roles.js";
import { useSession } from "./session.js";

const activeOrganizationStorageKey = "fleetminder.active-organization";

/**
 * The last organization is remembered per user, so signing in as someone else
 * on a shared machine never lands on the previous person's fleet.
 */
function storageKeyFor(userId: string): string {
  return `${activeOrganizationStorageKey}.${userId}`;
}

export function readLastActiveOrganization(userId: string): string | undefined {
  try {
    return window.localStorage.getItem(storageKeyFor(userId)) ?? undefined;
  } catch {
    return undefined;
  }
}

export function rememberActiveOrganization(
  userId: string,
  organizationId: string,
): void {
  try {
    window.localStorage.setItem(storageKeyFor(userId), organizationId);
  } catch {
    // Remembering the choice is a convenience; the route parameter is the
    // actual source of truth for which organization is in view.
  }
}

export function forgetActiveOrganization(userId: string): void {
  try {
    window.localStorage.removeItem(storageKeyFor(userId));
  } catch {
    // Nothing to recover from: the stored value is only ever a hint.
  }
}

/**
 * Where `/app` should land: the remembered organization when the user is still
 * a member of it, the only organization when there is exactly one, and the
 * picker otherwise.
 */
export function resolveLandingPath(
  organizations: readonly OrganizationMembership[],
  remembered: string | undefined,
): string {
  if (organizations.length === 0) {
    return "/app/onboarding/organization";
  }

  const rememberedMembership = organizations.find(
    (organization) => organization.organizationId === remembered,
  );

  if (rememberedMembership) {
    return `/app/organizations/${rememberedMembership.organizationId}`;
  }

  if (organizations.length === 1 && organizations[0]) {
    return `/app/organizations/${organizations[0].organizationId}`;
  }

  return "/app/organizations/select";
}

/**
 * The organization in view. For the sample fleet (REL-001), which nobody is
 * a member of, it reads as an administrator's view with no membership, and
 * `sample` says to label it and offer the way back.
 */
export type ActiveOrganization = Omit<
  OrganizationMembership,
  "membershipId"
> & {
  readonly membershipId: string | null;
  readonly sample: boolean;
};

/**
 * The read-only sample fleet, or null when this deployment has none. Kept
 * under the session's key, so signing out forgets it with everything else.
 */
export function useSampleOrganization(options: { readonly enabled: boolean }) {
  const { user } = useSession();
  return useQuery({
    enabled: options.enabled && Boolean(user),
    queryFn: async () => {
      try {
        return await callApi(getSampleOrganization);
      } catch (error) {
        if (error instanceof WebApiError && error.status === 404) return null;
        throw error;
      }
    },
    queryKey: ["session", "sample-organization"],
    retry: false,
    staleTime: 5 * 60_000,
  });
}

export function useActiveOrganization(): ActiveOrganization | undefined {
  const { organizationId } = useParams();
  const { organizations } = useSession();
  const membership = organizations.find(
    (organization) => organization.organizationId === organizationId,
  );
  const sample = useSampleOrganization({
    enabled: Boolean(organizationId) && !membership,
  });

  if (membership) return { ...membership, sample: false };
  if (sample.data && sample.data.organizationId === organizationId)
    return {
      membershipId: null,
      name: sample.data.name,
      organizationId: sample.data.organizationId,
      role: "admin",
      sample: true,
    };
  return undefined;
}

/**
 * Opens the sample fleet, dropping the current company's cache first, the
 * same as switching companies does. It is never remembered as the last
 * company, so the next visit lands back on the person's own.
 */
export function useExploreSample(): (() => void) | undefined {
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const { organizationId } = useParams();
  const sample = useSampleOrganization({ enabled: true });
  const target = sample.data?.organizationId;

  const explore = useCallback(() => {
    if (!target) return;
    if (organizationId)
      queryClient.removeQueries({
        queryKey: queryKeys.organization(organizationId),
      });
    navigate(`/app/organizations/${target}`);
  }, [navigate, organizationId, queryClient, target]);

  return target && target !== organizationId ? explore : undefined;
}

/**
 * On every page of the sample fleet: what it is, that changes are off, and
 * the one step back to the person's own company.
 */
function SampleDataBanner({
  organizationId,
}: {
  readonly organizationId: string;
}) {
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const { organizations, user } = useSession();

  // Straight back to the company they came from; the sample is never
  // remembered, so the remembered one is theirs.
  function leave() {
    queryClient.removeQueries({
      queryKey: queryKeys.organization(organizationId),
    });
    navigate(
      resolveLandingPath(
        organizations,
        user ? readLastActiveOrganization(user.id) : undefined,
      ),
    );
  }

  return (
    <Alert className="mb-6" role="region" aria-label="Sample fleet">
      <CompassIcon aria-hidden />
      <AlertTitle>Sample fleet</AlertTitle>
      <AlertDescription className="flex flex-wrap items-center gap-x-3 gap-y-2">
        <span>
          Made-up trucks so you can look around. You can&apos;t change anything
          here.
        </span>
        <Button onClick={leave} size="sm" variant="outline">
          {organizations.length > 0
            ? "Back to my company"
            : "Set up my own company"}
        </Button>
      </AlertDescription>
    </Alert>
  );
}

/**
 * Guards every `/app/organizations/:organizationId` route. A deep link to an
 * organization the caller is not a member of is not merely empty — it is sent
 * back to the picker, and the stored preference is dropped so the next visit
 * does not repeat the failure.
 */
export function RequireOrganizationMembership({ children }: PropsWithChildren) {
  const { organizationId } = useParams();
  const { isLoading, organizations, user } = useSession();
  const membership = organizations.find(
    (organization) => organization.organizationId === organizationId,
  );
  const sample = useSampleOrganization({
    enabled: !isLoading && !membership,
  });
  const inSample =
    !membership &&
    Boolean(organizationId) &&
    sample.data?.organizationId === organizationId;

  useEffect(() => {
    if (user && membership) {
      rememberActiveOrganization(user.id, membership.organizationId);
    }
  }, [membership, user]);

  if (isLoading || (!membership && sample.isPending)) {
    return (
      <div
        className="flex min-h-[40vh] items-center justify-center gap-3 text-sm text-muted-foreground"
        role="status"
      >
        <Spinner className="size-5" />
        Loading your company
      </div>
    );
  }

  if (inSample && organizationId) {
    return (
      <>
        <SampleDataBanner organizationId={organizationId} />
        {children}
      </>
    );
  }

  if (!membership) {
    if (user) {
      forgetActiveOrganization(user.id);
    }

    return <Navigate replace to="/app/organizations/select" />;
  }

  return children;
}

/** Sends `/app` to whichever organization the caller should see. */
export function OrganizationLanding() {
  const { isLoading, organizations, user } = useSession();

  if (isLoading || !user) {
    return (
      <div
        className="flex min-h-[40vh] items-center justify-center gap-3 text-sm text-muted-foreground"
        role="status"
      >
        <Spinner className="size-5" />
        Loading your company
      </div>
    );
  }

  return (
    <Navigate
      replace
      to={resolveLandingPath(
        organizations,
        readLastActiveOrganization(user.id),
      )}
    />
  );
}

export function useSwitchOrganization(): (organizationId: string) => void {
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const { organizationId } = useParams();
  const { user } = useSession();

  return useCallback(
    (nextOrganizationId: string) => {
      if (nextOrganizationId === organizationId) {
        return;
      }

      // Drop the previous organization's cache before navigating, so no row
      // from the old fleet can render for even one frame under the new one.
      if (organizationId) {
        queryClient.removeQueries({
          queryKey: queryKeys.organization(organizationId),
        });
      }

      if (user) {
        rememberActiveOrganization(user.id, nextOrganizationId);
      }

      navigate(`/app/organizations/${nextOrganizationId}`);
    },
    [navigate, organizationId, queryClient, user],
  );
}

export function OrganizationSwitcher() {
  const { organizations } = useSession();
  const navigate = useNavigate();
  const active = useActiveOrganization();
  const switchOrganization = useSwitchOrganization();
  const exploreSample = useExploreSample();
  const label = active?.name ?? "Choose a company";

  return (
    <DropdownMenu>
      <DropdownMenuTrigger asChild>
        <SidebarMenuButton size="lg">
          <div className="flex aspect-square size-8 items-center justify-center rounded-lg bg-primary text-primary-foreground">
            <BuildingIcon aria-hidden className="size-4" />
          </div>
          <div className="grid flex-1 text-left leading-tight">
            <span className="truncate font-semibold">{label}</span>
            <span className="truncate text-xs text-muted-foreground">
              {active?.sample
                ? "Sample fleet"
                : active
                  ? roleLabels[active.role]
                  : "FleetMinder"}
            </span>
          </div>
          <ChevronsUpDownIcon aria-hidden className="ml-auto" />
        </SidebarMenuButton>
      </DropdownMenuTrigger>
      <DropdownMenuContent align="start" className="w-60">
        <DropdownMenuLabel>Your companies</DropdownMenuLabel>
        <DropdownMenuSeparator />
        {organizations.map((organization) => (
          <DropdownMenuItem
            key={organization.organizationId}
            onSelect={() => switchOrganization(organization.organizationId)}
          >
            <span className="truncate">{organization.name}</span>
            <Badge className="ml-auto" variant="secondary">
              {roleLabels[organization.role]}
            </Badge>
            {organization.organizationId === active?.organizationId ? (
              <CheckIcon aria-hidden className="size-4" />
            ) : null}
          </DropdownMenuItem>
        ))}
        {organizations.length === 0 ? (
          <DropdownMenuItem disabled>No company yet</DropdownMenuItem>
        ) : null}
        <DropdownMenuSeparator />
        {exploreSample ? (
          <DropdownMenuItem onSelect={exploreSample}>
            <CompassIcon aria-hidden className="size-4" />
            Explore sample fleet
          </DropdownMenuItem>
        ) : null}
        {/* Someone running more than one fleet, or setting one up for a
            client, starts a new organization from here. */}
        <DropdownMenuItem
          onSelect={() => navigate("/app/onboarding/organization")}
        >
          <PlusIcon aria-hidden className="size-4" />
          Set up another company
        </DropdownMenuItem>
      </DropdownMenuContent>
    </DropdownMenu>
  );
}
